import { useEffect, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Link2 } from "lucide-react";
import { post } from "./api";
import { Button, Ring } from "./components";
import { creditNumber } from "../shared/catalog";
import type { Account, StudioConfig } from "../shared/types";
import { track } from "./usage";
import { bilingual, lang, locale, text, useText } from "./i18n";

const TEXT = bilingual({
  en: {
    redeemed: {
      added: (credits: string, until: string) =>
        `${credits} Señal credits added. They last until ${until}.`,
      already: "Your Señal credits are already in your account.",
    },
    panel: {
      includes: "Your Señal purchase includes",
      focoCredits: (credits: string) => `${credits} Foco credits`,
      createOrSignIn: "Create an account or sign in to add them.",
      signInToAdd: "Sign in to add them.",
      confirming: "Confirming your email…",
      back: "Back to sign in",
      checkEmail: "Check your email",
      verifySent: (email: string) => (
        <>
          We sent a link to <b>{email}</b>. Open it to confirm your email and
          sign in.
        </>
      ),
      resetSent: (email: string) => (
        <>
          If <b>{email}</b> has a Foco account, a link to choose a new password
          is on its way.
        </>
      ),
      sentAgain: "Sent again",
      sendAgain: "Send it again",
      newPasswordTitle: "Choose a new password",
      newPassword: "New password",
      newPasswordHint: "New password, 10+ characters",
      saving: "Saving…",
      saveAndSignIn: "Save and sign in",
      resetTitle: "Reset your password",
      email: "Email address",
      emailHint: "Email",
      sending: "Sending…",
      sendReset: "Send a reset link",
      password: "Password",
      passwordHint: "Password, 10+ characters",
      creating: "Creating your account…",
      signingIn: "Signing in…",
      createAccount: "Create account",
      yourLink: "Your link",
      signIn: "Sign in",
      // legal counsel's box (A10), unticked: both links lead to /legal
      accept: (terms: ReactNode, privacy: ReactNode) => (
        <>
          I have read and accept the {terms} and the {privacy}.
        </>
      ),
      terms: "terms of use",
      privacy: "privacy policy",
      haveAccount: "I have an account",
      toSignUp: "Create an account",
      forgot: "Forgot password?",
    },
    redeem: {
      add: (credits: string) => `Add ${credits} credits`,
      from: (email: string) => (
        <>
          From your Señal purchase, to <b>{email}</b>.
        </>
      ),
      adding: "Adding…",
      failed: "These credits can't be added",
      none: "No credit link here",
      openAgain:
        "Open the link from your Señal purchase again to add its credits.",
      studio: "Go to the studio",
    },
  },
  es: {
    redeemed: {
      added: (credits: string, until: string) =>
        `${credits} créditos de Señal añadidos. Duran hasta el ${until}.`,
      already: "Tus créditos de Señal ya están en tu cuenta.",
    },
    panel: {
      includes: "Tu compra en Señal incluye",
      focoCredits: (credits: string) => `${credits} créditos de Foco`,
      createOrSignIn: "Crea una cuenta o inicia sesión para añadirlos.",
      signInToAdd: "Inicia sesión para añadirlos.",
      confirming: "Confirmando tu correo…",
      back: "Volver a iniciar sesión",
      checkEmail: "Revisa tu correo",
      verifySent: (email: string) => (
        <>
          Te hemos enviado un enlace a <b>{email}</b>. Ábrelo para confirmar tu
          correo e iniciar sesión.
        </>
      ),
      resetSent: (email: string) => (
        <>
          Si <b>{email}</b> tiene una cuenta de Foco, te llegará un enlace para
          elegir una contraseña nueva.
        </>
      ),
      sentAgain: "Enviado de nuevo",
      sendAgain: "Enviarlo de nuevo",
      newPasswordTitle: "Elige una contraseña nueva",
      newPassword: "Contraseña nueva",
      newPasswordHint: "Contraseña nueva, mínimo 10 caracteres",
      saving: "Guardando…",
      saveAndSignIn: "Guardar e iniciar sesión",
      resetTitle: "Restablece tu contraseña",
      email: "Correo electrónico",
      emailHint: "Correo electrónico",
      sending: "Enviando…",
      sendReset: "Enviar enlace para restablecerla",
      password: "Contraseña",
      passwordHint: "Contraseña, mínimo 10 caracteres",
      creating: "Creando tu cuenta…",
      signingIn: "Iniciando sesión…",
      createAccount: "Crear cuenta",
      yourLink: "Tu enlace",
      signIn: "Iniciar sesión",
      accept: (terms: ReactNode, privacy: ReactNode) => (
        <>
          He leído y acepto las {terms} y la {privacy}.
        </>
      ),
      terms: "condiciones de uso",
      privacy: "política de privacidad",
      haveAccount: "Ya tengo cuenta",
      toSignUp: "Crear una cuenta",
      forgot: "¿Has olvidado tu contraseña?",
    },
    redeem: {
      add: (credits: string) => `Añadir ${credits} créditos`,
      from: (email: string) => (
        <>
          De tu compra en Señal, para <b>{email}</b>.
        </>
      ),
      adding: "Añadiendo…",
      failed: "No se pueden añadir estos créditos",
      none: "Aquí no hay ningún enlace de créditos",
      openAgain:
        "Vuelve a abrir el enlace de tu compra en Señal para añadir sus créditos.",
      studio: "Ir al estudio",
    },
  },
});
const credits = (n: number) => creditNumber(n, lang());

// A Señal credit link (/canjear?c=…) leaves the address bar at once and waits
// in this tab through sign-up or sign-in.
const HELD = "foco.redeem";
export function takeRedemption() {
  const url = new URL(window.location.href);
  const token = url.searchParams.get("c");
  if (!token) return;
  try {
    sessionStorage.setItem(HELD, token);
  } catch {
    /* Storage blocked: the link can be opened again. */
  }
  url.searchParams.delete("c");
  history.replaceState(history.state, "", url.pathname + url.search + url.hash);
}
export function heldRedemption() {
  try {
    return sessionStorage.getItem(HELD) || "";
  } catch {
    return "";
  }
}
function dropRedemption() {
  try {
    sessionStorage.removeItem(HELD);
  } catch {
    /* Nothing held. */
  }
}
/** The credits a link carries, read for display only; the studio checks the signature. */
export function linkCredits(token: string) {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    return Number.isInteger(payload.credits) ? (payload.credits as number) : 0;
  } catch {
    return 0;
  }
}
type Redeemed =
  | { state: "added" | "already"; credits: number; expires: string }
  | { error: string }
  | null;
const until = (iso: string) =>
  new Date(iso).toLocaleDateString(locale(), {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
/** What a confirmed sign-up says about the link it brought along. */
export function redeemedNotice(r: Redeemed) {
  if (!r) return "";
  dropRedemption();
  if ("error" in r) return r.error;
  const t = text(TEXT).redeemed;
  return r.state === "added"
    ? t.added(credits(r.credits), until(r.expires))
    : t.already;
}

type Mode = "signin" | "signup" | "forgot" | "sent" | "reset" | "verify";

/** Sign in, create an account, confirm an email or choose a new password. */
export function AccountPanel({
  config,
  onLogin,
}: {
  config: StudioConfig;
  onLogin: (a: Account, notice?: string) => void;
}) {
  const t = useText(TEXT).panel;
  const path = window.location.pathname,
    token = new URLSearchParams(window.location.search).get("t") || "",
    held = heldRedemption(),
    linked = held ? linkCredits(held) : 0;
  // what the visitor pasted on the landing: sent with the sign-up, and handed to the composer on sign-in
  const brief = String(useLocation().state?.brief || "").slice(0, 2000);
  const [mode, setMode] = useState<Mode>(
    token && path === "/verify"
      ? "verify"
      : token && path === "/reset"
        ? "reset"
        : (held || path === "/empezar") && config.signup
          ? "signup"
          : "signin",
  );
  const [email, setEmail] = useState(""),
    [sent, setSent] = useState<"verify" | "reset">("verify"),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [resent, setResent] = useState(false);
  const navigate = useNavigate();
  const go = (m: Mode) => {
    setError("");
    setMode(m);
  };
  // usage pings (src/usage.ts): each form started, sent or failed
  const ping = (k: string, m: string = mode) => track("form", `${m}: ${k}`);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      ping("failed");
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const field = (e: FormEvent<HTMLFormElement>, name: string) =>
    String(new FormData(e.currentTarget).get(name) || "");
  // A link from an email signs the account in, then the address is the studio's.
  const done = (r: {
    account: Account;
    redeemed?: Redeemed;
    brief?: string;
  }) => {
    // A link held since sign-up was redeemed by the studio; one only in this tab still waits.
    const notice = redeemedNotice(r.redeemed ?? null),
      draft = r.brief || brief;
    navigate(heldRedemption() ? "/canjear" : "/", {
      replace: true,
      state: draft ? { draft } : null,
    });
    onLogin(r.account, notice || undefined);
  };
  const started = useRef(false);
  useEffect(() => {
    if (mode !== "verify" || started.current) return;
    started.current = true;
    void run(() =>
      post("/auth/verify", { token }).then((r) => {
        ping("confirmed");
        done(r);
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const offer = held && linked > 0 && (
    <p className="welcome-offer">
      {t.includes} <b>{t.focoCredits(credits(linked))}</b>.{" "}
      {config.signup ? t.createOrSignIn : t.signInToAdd}
    </p>
  );
  const failure = error && (
    <p className="form-error" role="alert">
      {error}
    </p>
  );
  const switcher = (to: Mode, label: string) => (
    <button type="button" className="welcome-link" onClick={() => go(to)}>
      {label}
    </button>
  );

  if (mode === "verify")
    return (
      <div className="welcome-form">
        {busy ? (
          <p className="welcome-status" role="status">
            <Ring /> {t.confirming}
          </p>
        ) : (
          <>
            {failure}
            {switcher("signin", t.back)}
          </>
        )}
      </div>
    );
  if (mode === "sent")
    return (
      <div className="welcome-form">
        <h2 className="h2">{t.checkEmail}</h2>
        <p className="welcome-copy">
          {sent === "verify" ? t.verifySent(email) : t.resetSent(email)}
        </p>
        {failure}
        {sent === "verify" && (
          <Button
            className="ghost large full"
            disabled={busy || resent}
            onClick={() =>
              void run(async () => {
                await post("/auth/resend", { email });
                setResent(true);
              })
            }
          >
            {resent ? t.sentAgain : t.sendAgain}
          </Button>
        )}
        {switcher("signin", t.back)}
      </div>
    );
  if (mode === "reset")
    return (
      <form
        className="welcome-form"
        onSubmit={(e) => {
          e.preventDefault();
          const password = field(e, "password");
          void run(() =>
            post("/auth/reset", { token, password }).then((r) => {
              ping("sent");
              done(r);
            }),
          );
        }}
        onInput={() => ping("started")}
      >
        <h2 className="h2">{t.newPasswordTitle}</h2>
        <label className="field">
          <span className="sr-only">{t.newPassword}</span>
          <input
            name="password"
            type="password"
            placeholder={t.newPasswordHint}
            aria-label={t.newPassword}
            autoComplete="new-password"
            minLength={10}
            required
          />
        </label>
        {failure}
        <Button className="primary large full" disabled={busy}>
          {busy ? t.saving : t.saveAndSignIn}
          {!busy && <ArrowRight size={18} />}
        </Button>
      </form>
    );
  if (mode === "forgot")
    return (
      <form
        className="welcome-form"
        onSubmit={(e) => {
          e.preventDefault();
          const address = field(e, "email");
          void run(async () => {
            await post("/auth/forgot", { email: address });
            ping("sent");
            setEmail(address);
            setSent("reset");
            go("sent");
          });
        }}
        onInput={() => ping("started")}
      >
        <h2 className="h2">{t.resetTitle}</h2>
        <label className="field">
          <span className="sr-only">{t.email}</span>
          <input
            name="email"
            type="email"
            placeholder={t.emailHint}
            aria-label={t.email}
            autoComplete="email"
            defaultValue={email}
            required
          />
        </label>
        {failure}
        <Button className="primary large full" disabled={busy}>
          {busy ? t.sending : t.sendReset}
          {!busy && <ArrowRight size={18} />}
        </Button>
        {switcher("signin", t.back)}
      </form>
    );
  const signup = mode === "signup";
  return (
    <form
      className="welcome-form"
      onInput={() => ping("started")}
      onSubmit={(e) => {
        e.preventDefault();
        const address = field(e, "email"),
          password = field(e, "password"),
          // the terms box, which the browser already requires ticked
          accepted = field(e, "terms") === "on";
        setEmail(address);
        void run(async () => {
          if (signup) {
            await post("/auth/signup", {
              email: address,
              password,
              terms: accepted,
              ...(held ? { redeem: held } : {}),
              ...(brief ? { brief } : {}),
            });
            ping("sent");
            setSent("verify");
            go("sent");
            return;
          }
          try {
            const { account } = await post("/auth/login", {
              email: address,
              password,
            });
            ping("sent");
            if (brief)
              navigate("/", { replace: true, state: { draft: brief } });
            onLogin(account);
          } catch (e) {
            if ((e as { code?: string }).code !== "unverified") throw e;
            ping("unverified");
            setSent("verify");
            go("sent");
          }
        });
      }}
    >
      {offer}
      {brief && (
        <p className="welcome-brief" aria-label={t.yourLink}>
          <Link2 size={15} />
          <span>{brief.replace(/^https?:\/\/(www\.)?/i, "")}</span>
        </p>
      )}
      <label className="field">
        <span className="sr-only">{t.email}</span>
        <input
          name="email"
          type="email"
          placeholder={t.emailHint}
          aria-label={t.email}
          autoComplete="email"
          defaultValue={email}
          required
        />
      </label>
      <label className="field">
        <span className="sr-only">{t.password}</span>
        <input
          name="password"
          type="password"
          placeholder={signup ? t.passwordHint : t.password}
          aria-label={t.password}
          autoComplete={signup ? "new-password" : "current-password"}
          minLength={signup ? 10 : undefined}
          required
        />
      </label>
      {signup && (
        <label className="welcome-accept">
          <input name="terms" type="checkbox" required />
          <span>
            {t.accept(
              <a href="/legal#terms" target="_blank" rel="noopener">
                {t.terms}
              </a>,
              <a href="/legal#privacy" target="_blank" rel="noopener">
                {t.privacy}
              </a>,
            )}
          </span>
        </label>
      )}
      {failure}
      <Button className="primary large full" disabled={busy}>
        {busy
          ? signup
            ? t.creating
            : t.signingIn
          : signup
            ? t.createAccount
            : t.signIn}
        {!busy && <ArrowRight size={18} />}
      </Button>
      <div className="welcome-links">
        {signup
          ? switcher("signin", t.haveAccount)
          : config.signup && switcher("signup", t.toSignUp)}
        {!signup && switcher("forgot", t.forgot)}
      </div>
    </form>
  );
}

/** Adds a held Señal link's credits to the signed-in account. */
export function Redeem({
  account,
  onDone,
}: {
  account: Account;
  onDone: (notice: string) => void;
}) {
  const t = useText(TEXT).redeem;
  const held = heldRedemption(),
    linked = held ? linkCredits(held) : 0;
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const navigate = useNavigate();
  const redeem = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await post("/redeem/senal", { token: held });
      window.dispatchEvent(new Event("cutline:credits"));
      onDone(redeemedNotice(r));
      navigate("/", { replace: true });
    } catch (e) {
      setError((e as Error).message);
      if ([400, 409, 410].includes((e as { status?: number }).status ?? 0))
        dropRedemption();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="page page-enter redeem">
      <div className="card redeem-card">
        {held && linked > 0 && !error ? (
          <>
            <h1 className="h1">{t.add(credits(linked))}</h1>
            <p className="muted">{t.from(account.email)}</p>
            <Button
              className="primary large full"
              disabled={busy}
              onClick={() => void redeem()}
            >
              {busy ? t.adding : t.add(credits(linked))}
              {!busy && <ArrowRight size={18} />}
            </Button>
          </>
        ) : (
          <>
            <h1 className="h1">{error ? t.failed : t.none}</h1>
            <p className="muted">{error || t.openAgain}</p>
            <Button className="ghost large full" onClick={() => navigate("/")}>
              {t.studio}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
